/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/

import { DateTime } from 'luxon';

// The two spellings of a range bound, and the conversions between them.
//
// Typed form (`SpanFields`): what the picker's fields hold and a custom selection keeps in the
// URL. Grafana's date math ("now", "now-6h", "now/d" for today's midnight, "now-1w/w" for the
// previous ISO week's Monday, "now/d-7d"), a date ("2026-07-01") or a date-time
// ("2026-07-01 06:00"). A typed end date is inclusive: it means the whole of that day.
//
// Request form (`RangeWindow`): what the API receives. Relative expressions as typed, otherwise a
// date at midnight or an ISO instant, ends exclusive. Relative bounds are resolved against the
// clock as each request is sent (see `resolveWindow`), so a refetch of a live window moves with
// time while its query key stays put.

/** Which end of a span a bound names; a typed end date closes its day, a start opens it. */
export type BoundRole = 'from' | 'to';

/** A span in request form (see the module notes). */
export interface RangeWindow {
  from: string;
  to: string;
}

/** A span in typed form (see the module notes). */
export interface SpanFields {
  from: string;
  to: string;
}

export const DATE_FORMAT = 'yyyy-MM-dd';
const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm';
/** Also accepted when typed: the ISO spelling of a date-time. */
const DATE_TIME_FORMATS = [DATE_TIME_FORMAT, "yyyy-MM-dd'T'HH:mm"];

// "now" followed by any chain of steps ("-7d", "+1d") and roundings ("/d"), applied left to right
const RELATIVE = /^now((?:[+-]\d+[hdwMy]|\/[hdwMy])*)$/;
const OPERATION = /([+-])(\d+)([hdwMy])|\/([hdwMy])/g;
const UNITS = { h: 'hour', d: 'day', w: 'week', M: 'month', y: 'year' } as const;
type UnitLetter = keyof typeof UNITS;

const utc = (text: string, format: string): DateTime => DateTime.fromFormat(text, format, { zone: 'utc' });

/** Whether a moment sits on a UTC day boundary. */
export const isMidnight = (moment: DateTime): boolean => moment.equals(moment.startOf('day'));

/** A typed expression as kept in the URL: trimmed, "now" in lower case, the rest as typed. */
export function normalizeExpression(expression: string): string {
  return expression.trim().replace(/^now/i, 'now');
}

/** Whether an expression follows the clock ("now", "now-6h", "now/d"). */
export const isRelative = (expression: string): boolean => RELATIVE.test(normalizeExpression(expression));

function resolveRelative(text: string, now: DateTime): DateTime | undefined {
  const match = RELATIVE.exec(text);
  if (!match) {
    return undefined;
  }
  let moment = now.startOf('second');
  for (const [, sign, count, unit, round] of match[1].matchAll(OPERATION)) {
    moment =
      round !== undefined
        ? moment.startOf(UNITS[round as UnitLetter])
        : moment.plus({ [UNITS[unit as UnitLetter]]: (sign === '-' ? -1 : 1) * Number(count) });
  }
  return moment;
}

/**
 * The moment a typed expression names, or undefined when malformed. A date opens its day as a
 * start and closes it as an end (the next midnight, ends being exclusive).
 */
export function resolveExpression(
  expression: string,
  role: BoundRole,
  now: DateTime = DateTime.utc()
): DateTime | undefined {
  const text = normalizeExpression(expression);
  const relative = resolveRelative(text, now);
  if (relative) {
    return relative;
  }
  const dateTime = DATE_TIME_FORMATS.map((format) => utc(text, format)).find((moment) => moment.isValid);
  if (dateTime) {
    return dateTime;
  }
  const date = utc(text, DATE_FORMAT);
  if (!date.isValid) {
    return undefined;
  }
  return role === 'from' ? date : date.plus({ days: 1 });
}

/** The moment a request-form bound names, a relative one against the clock now. */
export function resolveBound(bound: string, now: DateTime = DateTime.utc()): DateTime {
  return resolveRelative(bound, now) ?? DateTime.fromISO(bound, { zone: 'utc' });
}

/** A moment in request form: a date when at midnight, otherwise an ISO instant. */
export function formatBound(moment: DateTime): string {
  return isMidnight(moment) ? moment.toFormat(DATE_FORMAT) : moment.toUTC().toISO({ suppressMilliseconds: true })!;
}

/** A window with its relative bounds resolved against the clock now, in request form. */
export function fixedWindow(window: RangeWindow, now: DateTime = DateTime.utc()): RangeWindow {
  return { from: formatBound(resolveBound(window.from, now)), to: formatBound(resolveBound(window.to, now)) };
}

/** A window's moments, relative bounds resolved against the clock now. */
export function resolveWindow(window: RangeWindow, now: DateTime = DateTime.utc()): { start: DateTime; end: DateTime } {
  return { start: resolveBound(window.from, now), end: resolveBound(window.to, now) };
}

/** A typed span in request form, or undefined when a bound is malformed or they are out of order. */
export function windowOf(fields: SpanFields, now: DateTime = DateTime.utc()): RangeWindow | undefined {
  const start = resolveExpression(fields.from, 'from', now);
  const end = resolveExpression(fields.to, 'to', now);
  if (start === undefined || end === undefined || start >= end) {
    return undefined;
  }
  return {
    from: isRelative(fields.from) ? normalizeExpression(fields.from) : formatBound(start),
    to: isRelative(fields.to) ? normalizeExpression(fields.to) : formatBound(end)
  };
}

/** Whether a typed span can be applied: both bounds resolve, in order. */
export const isValidSpan = (fields: SpanFields, now?: DateTime): boolean => windowOf(fields, now) !== undefined;

/** The other way round: a window in typed form, a midnight end as its last day. */
export function fieldsOf(window: RangeWindow): SpanFields {
  const typed = (bound: string, role: BoundRole): string => {
    if (isRelative(bound)) {
      return bound;
    }
    const moment = resolveBound(bound);
    if (!isMidnight(moment)) {
      return moment.toFormat(DATE_TIME_FORMAT);
    }
    return (role === 'to' ? moment.minus({ days: 1 }) : moment).toFormat(DATE_FORMAT);
  };
  return { from: typed(window.from, 'from'), to: typed(window.to, 'to') };
}

/** The span just before a window, of equal length (the growth baseline), in request form. */
export function precedingWindow(window: RangeWindow, now: DateTime = DateTime.utc()): RangeWindow {
  const { start, end } = resolveWindow(window, now);
  return { from: formatBound(start.minus(end.diff(start))), to: formatBound(start) };
}

/**
 * The earliest start the API serves: five years back. Floored to the hour so "now-5y", resolved
 * a moment earlier, still clears it.
 */
export function earliestFrom(now: DateTime = DateTime.utc()): DateTime {
  return now.startOf('hour').minus({ years: 5 });
}
